import { Children, cloneElement, isValidElement, Component } from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Tab } from './Tab';
import { TabBar } from './TabBar';
import { TabView } from './TabView';

const TabsElement = styled.div`
  color: #1a1d1f;
  display: grid;
  grid-template-areas: 'tabbar' 'tabview';
  grid-template-rows: auto 1fr;
  flex: 1;
  height: ${({ fillHeight }: any) => (fillHeight ? '100%' : 'auto')};
` as any;

export interface IProps {
  /** String representing the currently selected tab */
  selectedTabId?: string;
  /** Event handler fired when the user clicks on a tab */
  onSelectedTabChanged?: (tabId: string) => void;
  /** If true, displays content placeholder.  Defaults to false. */
  loading?: boolean;
  /** [Experimental] If true, applies 100% height to the tabs container which is useful in some layouts. */
  fillHeight?: boolean;
}

export interface IState {
  selectedTabId: string;
  sliderPos: number;
  sliderWidth: number;
}

export class Tabs extends Component<IProps, IState> {
  public state: IState = {
    selectedTabId: null,
    sliderPos: 0,
    sliderWidth: -1,
  };
  private selectedTabNode: any;
  private initialTabFound: boolean;

  public constructor(props: IProps) {
    super(props);
    this.state.selectedTabId = this.props.selectedTabId;
  }

  public componentDidMount() {
    if (this.selectedTabNode) {
      // eslint-disable-next-line react/no-find-dom-node
      const domNode: any = ReactDOM.findDOMNode(this.selectedTabNode);
      // If a selectedTabId prop is not explicitly specified, select the
      // first tab.
      const selectedTabId = this.state.selectedTabId || this.selectedTabNode.props.tabId;
      this.setState({
        selectedTabId,
        sliderPos: domNode.offsetLeft,
        sliderWidth: domNode.offsetWidth,
      });
    }
  }

  public componentDidUpdate(prevProps) {
    if (this.props.selectedTabId && this.props.selectedTabId !== prevProps.selectedTabId) {
      this.setState({
        selectedTabId: this.props.selectedTabId,
      });
    }
  }

  public render() {
    return (
      <TabsElement
        selectedTabId={this.props.selectedTabId}
        loading={this.props.loading}
        data-testid="TabsElement"
        fillHeight={this.props.fillHeight}
      >
        {this.renderTabBar()}
        {this.renderSelectedTabView()}
      </TabsElement>
    );
  }

  private renderTabBar() {
    let tabBar = null;
    Children.forEach(this.props.children, (child: any) => {
      if (child && child.type === TabBar) {
        tabBar = child;
      }
    });
    return tabBar ? this.enhancedTabBar(tabBar) : null;
  }

  private renderSelectedTabView() {
    let selectedTabView = null;
    // Search all children for the TabView that matches the selectedTabId.
    Children.forEach(this.props.children, (child: any) => {
      if (child && child.type === TabView && child.props.tabId === this.state.selectedTabId) {
        selectedTabView = child;
      }
    });
    // If no TabViews match, then assuming there are only two children (the TabBar and a single TabView),
    // make it the selected TabView even if it doesn't have a matching tabId.
    if (!selectedTabView && Children.count(this.props.children) === 2) {
      Children.forEach(this.props.children, (child: any) => {
        if (child && child.type === TabView && !child.props.tabId) {
          selectedTabView = child;
        }
      });
    }

    return selectedTabView;
  }

  private enhancedTabBar = tabBar => {
    // Return a clone of the tabBar where all tabs have an
    // onClick handler that sets the selectedTabId.
    tabBar = cloneElement(tabBar, {
      children: this.injectOnClickToAllChildTabs(tabBar),
      sliderPos: this.state.sliderPos,
      sliderWidth: this.state.sliderWidth,
      loading: this.props.loading,
    });
    return tabBar;
  };

  private injectOnClickToAllChildTabs = c => {
    return Children.map(c.props.children, (child: any) => {
      let onClick = null;
      let children = null;
      let ref = null;
      if (child && child.type === Tab) {
        onClick = (event: any, selectedTabId: string) => {
          this.setState({
            selectedTabId,
            sliderPos: event.target.offsetLeft,
            sliderWidth: event.target.offsetWidth,
          });
          if (this.props.onSelectedTabChanged) {
            this.props.onSelectedTabChanged(selectedTabId);
          }
          if (child.props.onClick) {
            child.props.onClick(event);
          }
        };

        if (!this.initialTabFound) {
          this.initialTabFound = true;
          ref = node => {
            this.selectedTabNode = node;
            if (typeof child.ref === 'function') {
              // Call the original ref, if any
              child.ref(node);
            }
          };
        }
        if (this.state.selectedTabId && this.state.selectedTabId === child.props.tabId) {
          ref = node => {
            this.selectedTabNode = node;
            if (typeof child.ref === 'function') {
              // Call the original ref, if any
              child.ref(node);
            }
          };
        }
      }
      if (isValidElement(child)) {
        children = this.injectOnClickToAllChildTabs(child);
      }
      if (onClick || children) {
        const selected = child.props.tabId === this.state.selectedTabId;
        const injectedProps: any = {
          children,
          selected,
          ref,
        };
        if (onClick) {
          injectedProps.onClick = onClick;
        }
        return cloneElement(child, injectedProps);
      } else {
        return child;
      }
    });
  };
}
